import './navbar.css';
import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { loadLied, subscribeLieder } from '../liederStore';
import { jugendKategorien } from '../buecher';

function toAudioSlug(value) {
  if (!value) return '';
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\u00DF/g, 'ss')
    .replace(/[^a-z0-9]+/g, '');
}

const audioContext = require.context('../audio', false, /\.mp3$/);
const audioFilesBySlug = audioContext.keys().reduce((result, filePath) => {
  const rawName = filePath.replace('./', '').replace(/\.mp3$/i, '');
  result[toAudioSlug(rawName)] = audioContext(filePath);
  return result;
}, {});

function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const isSongPage = location.pathname.startsWith('/lied/');
  const categories = jugendKategorien();
  const [showCategoryMenu, setShowCategoryMenu] = useState(false);
  const [showHamburgerMenu, setShowHamburgerMenu] = useState(false);
  const [audioSrc, setAudioSrc] = useState('');
  const [dataVersion, setDataVersion] = useState(0);
  const categoryMenuRef = useRef(null);
  const hamburgerMenuRef = useRef(null);
  const path = location.pathname;
  const isHome = path === '/';
  const isFavorites = path.startsWith('/favoriten');
  const isCategory = path.startsWith('/kategorie');
  const isSearch = path.startsWith('/suche');
  const isEdit = path.startsWith('/bearbeiten') || path.startsWith('/hinzufuegen');
  const selectedCategory = new URLSearchParams(location.search).get('kategorie') || '';

  useEffect(() => {
    setAudioSrc('');
  }, [location.pathname]);

  useEffect(() => {
    let cancelled = false;
    const songId = isSongPage ? location.pathname.split('/')[2] : '';
    if (!songId) return undefined;
    loadLied(songId)
      .then((record) => {
        if (cancelled) return;
        setAudioSrc(record?.audio_url || audioFilesBySlug[toAudioSlug(record?.name)] || '');
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isSongPage, location.pathname, dataVersion]);

  useEffect(() => {
    document.body.classList.toggle('has_audio', Boolean(audioSrc));
    return () => document.body.classList.remove('has_audio');
  }, [audioSrc]);

  useEffect(() => subscribeLieder(() => setDataVersion((v) => v + 1)), []);

  useEffect(() => {
    setShowCategoryMenu(false);
    setShowHamburgerMenu(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!showCategoryMenu && !showHamburgerMenu) return;
      if (event.target.closest('.nav_category') || event.target.closest('.nav_hamburger')) return;
      if (categoryMenuRef.current && !categoryMenuRef.current.contains(event.target)) {
        setShowCategoryMenu(false);
      }
      if (hamburgerMenuRef.current && !hamburgerMenuRef.current.contains(event.target)) {
        setShowHamburgerMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCategoryMenu, showHamburgerMenu]);

  function handleCategorySelect(category) {
    const params = new URLSearchParams();
    if (category) {
      params.set('kategorie', category);
    }

    const search = params.toString();
    navigate({
      pathname: '/kategorie',
      search: search ? `?${search}` : ''
    });
    setShowCategoryMenu(false);
  }

  function handleHamburgerToggle() {
    setShowHamburgerMenu((prev) => {
      if (!prev) setShowCategoryMenu(false);
      return !prev;
    });
  }

  function handleAddSong() {
    setShowHamburgerMenu(false);
    navigate('/hinzufuegen');
  }

  function handleEditSong() {
    setShowHamburgerMenu(false);
    navigate('/bearbeiten');
  }

  return (
    <>
      {audioSrc && (
        <div className='audio_bar'>
          <audio key={audioSrc} className='audio_player' src={audioSrc} controls />
        </div>
      )}
      <div className={`category_panel${showCategoryMenu ? ' is_open' : ''}`} ref={categoryMenuRef}>
          {categories.map((category) => (
            <button
              key={category.slug}
              type='button'
              className={`category_item${selectedCategory === category.slug ? ' is_active' : ''}`}
              onClick={() => handleCategorySelect(category.slug)}
            >
              {category.label}
            </button>
          ))}
      </div>
      <div ref={hamburgerMenuRef} className={`hamburger_menu${showHamburgerMenu ? ' is_open' : ''}`}>
        <button type='button' className='hamburger_menu_item' onClick={handleEditSong}>
          Lied bearbeiten
        </button>
        <button type='button' className='hamburger_menu_item' onClick={handleAddSong}>
          Lied hinzufügen
        </button>
      </div>
      <div id='navbar_body'>
        <Link className={`nav_item${isHome ? ' is_current' : ''}`} to='/' aria-label='Startseite'>
          <span className='nav_icon'>home</span>
          <span className='nav_label'>Home</span>
        </Link>
        <Link className={`nav_item${isFavorites ? ' is_current' : ''}`} to='/favoriten' aria-label='Favoriten'>
          <span className='nav_icon'>favorite</span>
          <span className='nav_label'>Favoriten</span>
        </Link>
        <button
          type='button'
          className={`nav_item nav_category${showCategoryMenu ? ' is_open' : ''}${isCategory ? ' is_current' : ''}`}
          aria-label='Kategorien'
          onClick={() => {
            setShowCategoryMenu((prev) => {
              if (!prev) setShowHamburgerMenu(false);
              return !prev;
            });
          }}
        >
          <span className='nav_icon'>category</span>
          <span className='nav_label'>Kategorien</span>
        </button>
        <Link className={`nav_item${isSearch ? ' is_current' : ''}`} to='/suche' aria-label='Suche'>
          <span className='nav_icon'>search</span>
          <span className='nav_label'>Suche</span>
        </Link>
        <button
          type='button'
          className={`nav_item nav_hamburger${showHamburgerMenu ? ' is_open' : ''}${isEdit ? ' is_current' : ''}`}
          aria-label='Bearbeiten'
          onClick={handleHamburgerToggle}
        >
          <span className='nav_icon'>edit</span>
          <span className='nav_label'>Bearbeiten</span>
        </button>
      </div>
    </>
  );
}

export default Navbar;
